import React, { useState } from 'react';
import classNames from 'classnames';
import './AudienceSlideoutMenu.pcss';
import { useSelector } from 'react-redux';
import { first, isEmpty } from 'lodash';
import { AudiencePreviewLazy } from './AudiencePreviewLazy/AudiencePreviewLazy';
import { SlideoutMenu } from '../../../../../components/SlideoutMenu/components/SlideoutMenu';
import { AudienceFilters } from './AudienceFilters';
import { AudienceNameInput } from './AudienceNameInput/AudienceNameInput';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useCreateNewAudience } from '../use-create-new-audience';
import AdAudience from '../../../../contacts/panels/CreateAudiencePanel/AdAudience';
import { StoreState } from '@mm/state/store-interfaces';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { useDetectOutsideClicks } from '../../../../../../common/hooks/use-detect-outside-clicks';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useLeadContactsFilter } from '../use-lead-contacts-filter';
import { InfoIcon } from '@mm/icons';
import { Heading } from '@mm/components';

type Props = {
  className?: string;
  audienceId?: number;
  newAudience?: boolean;
  onClose?: (audienceId?: number) => void;
  onCancel?: () => void;
  createBtnTitle?: string;
  title?: string;
};

export const AudienceSlideoutMenu = ({
  className,
  audienceId,
  onClose = () => {},
  onCancel = () => {},
  newAudience,
  createBtnTitle = 'Create audience',
  title,
}: Props) => {
  const { hideAudienceSlideoutMenu } = useCreateNewAudience();
  const { setAudienceId } = useLeadContactsFilter();
  const audience = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId || null),
  );
  const adAudience = first(audience)?.adAudience;
  const { usedInActiveDrip, campaignName } = audience[0] || {};
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);
  const { deleteAudience } = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [nameChanged, setNameChanged] = useState(false);
  const isEmptyFilters =
    isEmpty(audience[0]?.filterParams) && isEmpty(audience[0]?.tagsFilters);
  const shouldSaveAudience = nameChanged || !isEmptyFilters;

  const onDeleteAudience = async () => {
    if (onCancel) {
      await onCancel();
    }
    if (audienceId) {
      await setAudienceId(undefined);
      await deleteAudience(activeChatbotId, audienceId);
    }
  };

  const onCloseHandler = async () => {
    if (audienceId && newAudience && !shouldSaveAudience) {
      await onDeleteAudience();
    } else {
      await onClose(audienceId);
    }
  };

  const onCloseIconClick = async () => {
    hideAudienceSlideoutMenu();
    if (newAudience) {
      await onDeleteAudience();
    } else {
      await onClose(audienceId);
    }
  };

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };
  useDetectOutsideClicks(() => {
    hideAudienceSlideoutMenu();
    onCloseHandler();
  }, wrapperRef);

  if (!audienceId) return null;

  return (
    <SlideoutMenu
      className={classNames(className, 'audience-slideout-menu')}
      setRef={setRef}
    >
      <SlideoutMenu.Header onClose={onCloseIconClick}>
        {title ? (
          <Heading size={3} className="audience-slideout-menu__title">
            {title}
          </Heading>
        ) : (
          <AudienceNameInput
            className="audience-slideout-menu__name-input"
            audienceId={audienceId}
            setNameChanged={setNameChanged}
            usedInActiveDrip={usedInActiveDrip}
          />
        )}

        {adAudience && (
          <AdAudience
            className="audience-slideout-menu__sync-status"
            remoteId={adAudience.remoteId}
            audienceId={audienceId}
          />
        )}
      </SlideoutMenu.Header>
      {usedInActiveDrip && (
        <div className="audience-slideout-menu__notice">
          <InfoIcon
            style={{
              flex: 'none',
              width: '20px',
              height: '20px',
              marginRight: '10px',
            }}
          />
          <span>
            This audience is used in active drip campaign{' '}
            <strong>{campaignName}</strong>. Pause the campaign to edit filters
            of this audience
          </span>
        </div>
      )}
      <SlideoutMenu.Content className="audience-slideout-menu__content">
        <AudienceFilters
          className="audience-slideout-menu__filters"
          audienceId={audienceId}
        />
        <AudiencePreviewLazy
          className="audience-slideout-menu__preview"
          audienceId={audienceId}
        />
      </SlideoutMenu.Content>
      <div className="audience-slideout-menu__footer">
        {!newAudience ? (
          <PrimaryButton
            onClick={() => {
              hideAudienceSlideoutMenu();
              onClose(audienceId);
            }}
          >
            Done
          </PrimaryButton>
        ) : (
          <>
            <PrimaryButton
              className="audience-slideout-menu__footer-button"
              color={ButtonColor.secondaryBlue}
              onClick={() => {
                hideAudienceSlideoutMenu();
                onDeleteAudience();
              }}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              onClick={() => {
                hideAudienceSlideoutMenu();
                onClose(audienceId);
              }}
            >
              {createBtnTitle}
            </PrimaryButton>
          </>
        )}
      </div>
    </SlideoutMenu>
  );
};
